import React, { useState, useEffect, useRef, useCallback } from 'react';
import { loadModelViewer } from '../utils/loadModelViewer';
import './VisorRA.css';
import { FaRulerVertical, FaRulerHorizontal, FaTimes, FaArrowsAlt, FaRulerCombined, FaFileUpload } from 'react-icons/fa';

const VisorRA = () => {
  // Estados principales
  const [modelUrl, setModelUrl] = useState('');
  const [isViewerReady, setIsViewerReady] = useState(false);
  const [error, setError] = useState(null);
  const [measurementMode, setMeasurementMode] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(null);
  const [isInAR, setIsInAR] = useState(false);
  const [arSession, setArSession] = useState(null);
  const [showCrosshair, setShowCrosshair] = useState(false);
  const [isLoadingModel, setIsLoadingModel] = useState(false);
  
  // Referencias
  const modelViewerRef = useRef(null);
  const startPointRef = useRef(null);
  const hitTestSourceRef = useRef(null);
  const lastTapTimeRef = useRef(0);
  const arToolbarRef = useRef(null);
  const fileInputRef = useRef(null);
  const currentModelUrlRef = useRef('');

  // Cargar el visor 3D
  useEffect(() => {
    const loadViewer = async () => {
      try {
        const loaded = await loadModelViewer();
        if (!loaded) {
          throw new Error('No se pudo cargar el visor 3D. Asegúrese de estar usando un navegador compatible.');
        }
        setIsViewerReady(true);
      } catch (err) {
        setError(err.message);
        console.error('Error al cargar el visor:', err);
      }
    };

    loadViewer();

    return () => {
      // Limpiar URLs de objetos al desmontar
      if (currentModelUrlRef.current) {
        URL.revokeObjectURL(currentModelUrlRef.current);
      }
    };
  }, []);

  // Actualizar modelo cuando cambia la URL
  useEffect(() => {
    if (modelViewerRef.current && modelUrl) {
      setIsLoadingModel(true);
      
      const onLoad = () => {
        setIsLoadingModel(false);
      };
      
      const onError = () => {
        setIsLoadingModel(false);
        setError('Error al cargar el modelo. Verifique el archivo e intente nuevamente.');
      };
      
      modelViewerRef.current.addEventListener('load', onLoad);
      modelViewerRef.current.addEventListener('error', onError);
      
      // Actualizar la referencia de la URL actual
      if (currentModelUrlRef.current !== modelUrl) {
        if (currentModelUrlRef.current) {
          URL.revokeObjectURL(currentModelUrlRef.current);
        }
        currentModelUrlRef.current = modelUrl;
      }
      
      modelViewerRef.current.src = modelUrl;
      resetMeasurementState();
      
      return () => {
        modelViewerRef.current?.removeEventListener('load', onLoad);
        modelViewerRef.current?.removeEventListener('error', onError);
      };
    }
  }, [modelUrl]);

  // Reiniciar estado de mediciones
  const resetMeasurementState = useCallback(() => {
    setMeasurements([]);
    setMeasurementMode(null);
    setIsMeasuring(false);
    setCursorPosition(null);
    setShowCrosshair(false);
    startPointRef.current = null;
  }, []);

  // Calcular distancia entre puntos
  const calculateDistance = useCallback((start, end, type) => {
    if (!start || !end) return "0 m";
    
    let distance;
    switch (type) {
      case 'horizontal':
        distance = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.z - start.z, 2));
        break;
      case 'vertical':
        distance = Math.abs(end.y - start.y);
        break;
      default: // distancia 3D
        distance = Math.sqrt(
          Math.pow(end.x - start.x, 2) + 
          Math.pow(end.y - start.y, 2) + 
          Math.pow(end.z - start.z, 2)
        );
    }
    
    return distance >= 1 ? 
      `${Math.round(distance * 100) / 100} m` : 
      `${Math.round(distance * 100)} cm`;
  }, []);

  // Añadir nueva medición
  const addMeasurement = useCallback((endPosition) => {
    if (!startPointRef.current || !measurementMode) return;
    
    const newMeasurement = {
      id: Date.now() + Math.random().toString(36).substr(2, 9),
      start: {...startPointRef.current},
      end: {...endPosition},
      type: measurementMode,
      distance: calculateDistance(startPointRef.current, endPosition, measurementMode),
      isAR: isInAR
    };
    
    setMeasurements(prev => [...prev, newMeasurement]);
    startPointRef.current = null;
    setIsMeasuring(false);
  }, [calculateDistance, isInAR, measurementMode]);

  // Manejar AR
  const setupARSession = useCallback(async (session) => {
    setIsInAR(true);
    setArSession(session);
    resetMeasurementState();
    
    try {
      const viewer = modelViewerRef.current;
      const hitTestSource = await session.requestHitTestSource({
        space: viewer.renderer.xr.getReferenceSpace()
      });
      hitTestSourceRef.current = hitTestSource;
      
      // Mostrar toolbar en AR
      if (arToolbarRef.current) {
        arToolbarRef.current.style.display = 'flex';
      }
    } catch (err) {
      console.error('Error al configurar AR:', err);
      setError('No se pudo iniciar la sesión AR. Intente nuevamente.');
    }
  }, [resetMeasurementState]);

  // Manejar medición en AR
  const handleARMeasurement = useCallback(async () => {
    if (!arSession || !hitTestSourceRef.current || !measurementMode) return;
    
    try {
      const frame = await arSession.requestAnimationFrame();
      const hitTestResults = frame.getHitTestResults(hitTestSourceRef.current);
      
      if (hitTestResults.length > 0) {
        const hit = hitTestResults[0];
        const hitMatrix = hit.getPose(
          modelViewerRef.current.renderer.xr.getReferenceSpace()
        ).transform.matrix;
        
        const hitPosition = {
          x: hitMatrix[12],
          y: hitMatrix[13],
          z: hitMatrix[14]
        };
        
        if (!isMeasuring) {
          startPointRef.current = hitPosition;
          setIsMeasuring(true);
        } else {
          addMeasurement(hitPosition);
        }
      }
    } catch (err) {
      console.error('Error en medición AR:', err);
    }
  }, [arSession, isMeasuring, addMeasurement, measurementMode]);

  // Configurar eventos AR
  useEffect(() => {
    const viewer = modelViewerRef.current;
    if (!viewer) return;

    const handleARStatus = (event) => {
      if (event.detail.status === 'session-started') {
        setupARSession(viewer.arSession);
      } else if (event.detail.status === 'session-ended') {
        setIsInAR(false);
        setArSession(null);
        hitTestSourceRef.current = null;
        
        // Ocultar toolbar en AR al salir
        if (arToolbarRef.current) {
          arToolbarRef.current.style.display = 'none';
        }
      }
    };

    const handleARTap = (event) => {
      if (!isInAR || !measurementMode) return;
      
      const now = Date.now();
      if (now - lastTapTimeRef.current < 300) return; // Debounce
      lastTapTimeRef.current = now;
      
      handleARMeasurement();
    };

    viewer.addEventListener('ar-status', handleARStatus);
    viewer.addEventListener('touchstart', handleARTap);
    
    return () => {
      viewer.removeEventListener('ar-status', handleARStatus);
      viewer.removeEventListener('touchstart', handleARTap);
    };
  }, [isInAR, measurementMode, setupARSession, handleARMeasurement]);

  // Manejar interacción en modo escritorio
  const handleDesktopInteraction = useCallback((event) => {
    if (isInAR || !measurementMode) return;

    const viewer = modelViewerRef.current;
    const hit = viewer.positionAndNormalFromPoint(event.clientX, event.clientY);
    
    if (!hit?.position) return;

    if (!isMeasuring) {
      startPointRef.current = hit.position;
      setIsMeasuring(true);
    } else {
      addMeasurement(hit.position);
    }
  }, [isInAR, measurementMode, isMeasuring, addMeasurement]);

  // Manejar movimiento del cursor
  const handleCursorMove = useCallback((event) => {
    if (isInAR) return;

    const viewer = modelViewerRef.current;
    const hit = viewer.positionAndNormalFromPoint(event.clientX, event.clientY);
    setCursorPosition(hit?.position || null);
  }, [isInAR]);

  // Configurar eventos de escritorio
  useEffect(() => {
    const viewer = modelViewerRef.current;
    if (!viewer || isInAR) return;

    viewer.addEventListener('mousemove', handleCursorMove);
    
    if (measurementMode) {
      viewer.addEventListener('click', handleDesktopInteraction);
      setShowCrosshair(true);
    } else {
      viewer.removeEventListener('click', handleDesktopInteraction);
      setShowCrosshair(false);
    }
    
    return () => {
      viewer.removeEventListener('mousemove', handleCursorMove);
      viewer.removeEventListener('click', handleDesktopInteraction);
    };
  }, [measurementMode, isInAR, handleCursorMove, handleDesktopInteraction]);

  // Cargar modelo 3D
  const handleModelLoad = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Limpiar estado anterior
    resetMeasurementState();
    setError(null);

    // Validar formato
    const extension = file.name.split('.').pop().toLowerCase();
    if (!['glb', 'gltf'].includes(extension)) {
      setError('Formato no soportado. Use archivos .glb o .gltf');
      return;
    }

    try {
      setIsLoadingModel(true);
      const objectUrl = URL.createObjectURL(file);
      setModelUrl(objectUrl);
    } catch (err) {
      setError('Error al cargar el modelo');
      console.error(err);
      setIsLoadingModel(false);
    }
  };

  // Controlar modo de medición
  const toggleMeasurementMode = (mode) => {
    if (measurementMode === mode) {
      setMeasurementMode(null);
      setShowCrosshair(false);
    } else {
      setMeasurementMode(mode);
      setShowCrosshair(true);
    }
    startPointRef.current = null;
    setIsMeasuring(false);
  };

  // Controlar AR
  const toggleAR = () => {
    const viewer = modelViewerRef.current;
    if (!viewer) return;

    if (isInAR) {
      viewer.exitPresent();
    } else {
      viewer.activateAR();
    }
  };

  // Abrir selector de archivos
  const triggerFileInput = () => {
    fileInputRef.current.value = ''; // Resetear el input para permitir cargar el mismo archivo otra vez
    fileInputRef.current?.click();
  };

  // Renderizar mediciones
  const renderMeasurements = () => {
    return measurements.map((m) => {
      const midX = (m.start.x + m.end.x) / 2;
      const midY = (m.start.y + m.end.y) / 2;
      const midZ = (m.start.z + m.end.z) / 2;

      return (
        <div key={m.id} className={`measurement ${m.isAR ? 'ar' : ''}`}>
          <div className="measurement-line" style={{
            '--start-x': m.start.x,
            '--start-y': m.start.y,
            '--start-z': m.start.z,
            '--end-x': m.end.x,
            '--end-y': m.end.y,
            '--end-z': m.end.z,
          }}>
            <div className="measurement-label" style={{
              '--x': midX,
              '--y': midY,
              '--z': midZ,
            }}>
              {m.distance}
            </div>
          </div>
        </div>
      );
    });
  };

  // Renderizar cruz de medición
  const renderCrosshair = () => {
    if (!showCrosshair || !cursorPosition || isInAR) return null;

    return (
      <div className="crosshair" style={{
        '--x': cursorPosition.x,
        '--y': cursorPosition.y,
        '--z': cursorPosition.z,
      }}>
        <div className="crosshair-horizontal"></div>
        <div className="crosshair-vertical"></div>
      </div>
    );
  };

  // Renderizar medición en progreso
  const renderActiveMeasurement = () => {
    if (!isMeasuring || !startPointRef.current) return null;

    const endPosition = cursorPosition;
    if (!endPosition) return null;

    const midX = (startPointRef.current.x + endPosition.x) / 2;
    const midY = (startPointRef.current.y + endPosition.y) / 2;
    const midZ = (startPointRef.current.z + endPosition.z) / 2;

    return (
      <div className="measurement active">
        <div className="measurement-line" style={{
          '--start-x': startPointRef.current.x,
          '--start-y': startPointRef.current.y,
          '--start-z': startPointRef.current.z,
          '--end-x': endPosition.x,
          '--end-y': endPosition.y,
          '--end-z': endPosition.z,
        }}>
          <div className="measurement-label" style={{
            '--x': midX,
            '--y': midY,
            '--z': midZ,
          }}>
            {calculateDistance(startPointRef.current, endPosition, measurementMode)}
          </div>
        </div>
      </div>
    );
  };

  // Renderizar toolbar para AR
  const renderARToolbar = () => {
    return (
      <div className="ar-toolbar" ref={arToolbarRef}>
        <button
          className={`tool-button ${measurementMode === 'horizontal' ? 'active' : ''}`}
          onClick={() => toggleMeasurementMode('horizontal')}
          title="Medición horizontal"
        >
          <FaRulerHorizontal />
          <span className="tooltip">Horizontal</span>
        </button>
        <button
          className={`tool-button ${measurementMode === 'vertical' ? 'active' : ''}`}
          onClick={() => toggleMeasurementMode('vertical')}
          title="Medición vertical"
        >
          <FaRulerVertical />
          <span className="tooltip">Vertical</span>
        </button>
        <button
          className={`tool-button ${measurementMode === '3d' ? 'active' : ''}`}
          onClick={() => toggleMeasurementMode('3d')}
          title="Medición 3D"
        >
          <FaRulerCombined />
          <span className="tooltip">3D</span>
        </button>
        <button
          className="tool-button"
          onClick={toggleAR}
          title="Salir de AR"
        >
          <FaArrowsAlt />
          <span className="tooltip">Salir AR</span>
        </button>
        <button
          className="tool-button danger"
          onClick={resetMeasurementState}
          title="Borrar mediciones"
          disabled={measurements.length === 0 && !isMeasuring}
        >
          <FaTimes />
          <span className="tooltip">Borrar</span>
        </button>
      </div>
    );
  };

  // Renderizar toolbar principal
  const renderMainToolbar = () => {
    return (
      <div className="main-toolbar">
        <button
          className="tool-button upload-button"
          onClick={triggerFileInput}
          title="Cargar modelo"
        >
          <FaFileUpload />
          <span className="tooltip">Cargar modelo</span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".glb,.gltf"
            onChange={handleModelLoad}
            style={{ display: 'none' }}
          />
        </button>
        <div className="tool-separator"></div>
        <button
          className={`tool-button ${measurementMode === 'horizontal' ? 'active' : ''}`}
          onClick={() => toggleMeasurementMode('horizontal')}
          title="Medición horizontal"
          disabled={!modelUrl}
        >
          <FaRulerHorizontal />
          <span className="tooltip">Horizontal</span>
        </button>
        <button
          className={`tool-button ${measurementMode === 'vertical' ? 'active' : ''}`}
          onClick={() => toggleMeasurementMode('vertical')}
          title="Medición vertical"
          disabled={!modelUrl}
        >
          <FaRulerVertical />
          <span className="tooltip">Vertical</span>
        </button>
        <button
          className={`tool-button ${measurementMode === '3d' ? 'active' : ''}`}
          onClick={() => toggleMeasurementMode('3d')}
          title="Medición 3D"
          disabled={!modelUrl}
        >
          <FaRulerCombined />
          <span className="tooltip">3D</span>
        </button>
        <div className="tool-separator"></div>
        <button
          className="tool-button"
          onClick={toggleAR}
          title="Entrar en AR"
          disabled={!modelUrl}
        >
          <FaArrowsAlt />
          <span className="tooltip">AR</span>
        </button>
        <button
          className="tool-button danger"
          onClick={resetMeasurementState}
          title="Borrar mediciones"
          disabled={measurements.length === 0 && !isMeasuring}
        >
          <FaTimes />
          <span className="tooltip">Borrar</span>
        </button>
      </div>
    );
  };

  if (error) {
    return (
      <div className="error-container">
        <h1>Error</h1>
        <p>{error}</p>
        <button className="retry-button" onClick={() => setError(null)}>Reintentar</button>
      </div>
    );
  }

  if (!isViewerReady) {
    return (
      <div className="loading-container">
        <h1>Cargando visor 3D...</h1>
        <div className="spinner"></div>
        <p>Esto puede tomar unos segundos la primera vez</p>
      </div>
    );
  }

  return (
    <div className={`visor-container ${isInAR ? 'ar-mode' : ''}`}>
      {!isInAR && (
        <header className="visor-header">
          <h1>Visor de Realidad Aumentada para Ingeniería</h1>
          <p>Visualiza y mide modelos 3D en el navegador o en realidad aumentada</p>
        </header>
      )}

      {renderMainToolbar()}
      {isInAR && renderARToolbar()}

      <div className="model-viewer-container">
        {modelUrl ? (
          <>
            <model-viewer
              ref={modelViewerRef}
              src={modelUrl}
              ar
              ar-modes="webxr scene-viewer quick-look"
              camera-controls
              environment-image="neutral"
              exposure="1.0"
              shadow-intensity="1"
              bounds="tight"
              alt="Modelo 3D"
              style={{ cursor: measurementMode ? 'crosshair' : 'grab' }}
              loading={isLoadingModel ? 'eager' : 'lazy'}
            >
              {!isInAR && (
                <button slot="ar-button" className="ar-button">
                  👁️ Ver en Realidad Aumentada
                </button>
              )}
              
              {isLoadingModel && (
                <div slot="loading-overlay" className="loading-overlay">
                  <div className="loading-spinner"></div>
                  <p>Cargando modelo...</p>
                </div>
              )}
            </model-viewer>

            <div className="measurements-container">
              {renderActiveMeasurement()}
              {renderCrosshair()}
              {renderMeasurements()}
            </div>

            {isMeasuring && (
              <div className="measurement-instruction">
                {isInAR 
                  ? "Toque una superficie para marcar el segundo punto" 
                  : "Haga clic para marcar el segundo punto"}
              </div>
            )}
          </>
        ) : (
          !isInAR && (
            <div className="placeholder-container">
              <div className="placeholder-content">
                <FaFileUpload className="placeholder-icon" />
                <h2>No hay modelo cargado</h2>
                <p>Seleccione un archivo GLB o GLTF para comenzar</p>
                <button 
                  className="upload-button"
                  onClick={triggerFileInput}
                >
                  Seleccionar modelo
                </button>
              </div>
            </div>
          )
        )}
      </div>

      {!isInAR && (
        <footer className="visor-footer">
          <p>Visor RA para Ingeniería &copy; {new Date().getFullYear()}</p>
          <p>Usa las herramientas de medición para tomar dimensiones del modelo</p>
          <p>Suriyaco Valley - La Rioja Argentina</p>
        </footer>
      )}
    </div>
  );
};

export default VisorRA;